import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Container,
  FormikWrapper,
  FormSelect,
  Image,
} from '@uncinc/uncinc-react-kitchen-sink';

import { CoreLink, Paragraph, Taxonomy } from '@uncinc/react-drupal-core';
import { viewGet } from '@uncinc/react-drupal-core/dist/redux/actions/Entity/View';

import WysiwygField from '../../../../components/WysiwygField';
import Share from '../../../../components/Share';
import CampaignBlock from '../../../../components/Campaign/CampaignBlock';
import NodeMetadata from '../../../../components/NodeMetadata';
import PropertiesTable from '../../../../components/Molecules/PropertiesTable';

import Faq from '../../../../components/Molecules/Faq';
import MemberWall from '../../../../components/MemberWall';
import GetStarted from '../../../../components/Molecules/GetStarted';
import { SCREEN_MOBILE, SCREEN_NORMAL } from '../../../../config/constants';
import useLoggedInStatus from '../../../../hooks/useLoggedInstatus';
import useAddToCart from '../../../../hooks/useAddToCart';
import Training from './Training';


import './style.scss';
import Tags from '../../../../components/Tags';

const DateString = (dates, location) => {
  let dateString;

  // multiple time events
  if (dates.size > 1) {
    dates.map((date, i) => {
      const dateStart = new Date(Date.parse(date.getIn(['value'])));
      if (i === 0) {
        dateString = `${dateStart.getDate()} ${dateStart.toLocaleDateString(
          'nl-NL',
          { month: 'short' },
        )}`;
      } else if (i === dates.size - 1) {
        dateString += ` en ${dateStart.getDate()} ${dateStart.toLocaleDateString(
          'nl-NL',
          { month: 'short' },
        )}`;
      } else {
        dateString += `, ${dateStart.getDate()} ${dateStart.toLocaleDateString(
          'nl-NL',
          { month: 'short' },
        )}`;
      }
    });
    // single time events
  } else {
    const dateStart = new Date(Date.parse(dates.getIn([0, 'value'])));
    const dateEnd = new Date(Date.parse(dates.getIn([0, 'end_value'])));
    dateString = `${dateStart.toLocaleDateString('nl-NL', {
      weekday: 'short',
    })} ${dateStart.getDate()} ${dateStart.toLocaleDateString('nl-NL', {
      month: 'long',
    })} ${dateStart.getHours()}:${dateStart
      .getMinutes()
      .toString()
      .padStart(2, '0')} - ${dateEnd.getHours()}:${dateEnd
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;

    // single time events ending on another day
    const sameDayCheck = dateStart.getDate() === dateEnd.getDate();
    if (!sameDayCheck) {
      dateString = `${dateStart.toLocaleDateString('nl-NL', {
        weekday: 'short',
      })} ${dateStart.getDate()} ${dateStart.toLocaleDateString('nl-NL', {
        month: 'long',
      })} - ${dateEnd.toLocaleDateString('nl-NL', {
        weekday: 'short',
      })} ${dateEnd.getDate()} ${dateEnd.toLocaleDateString('nl-NL', {
        month: 'long',
      })}`;
    }
  }

  return `${dateString}<br/>${location}`;
};

const AMOUNT_UNLIMITED = 0;

const NodeEventDetail = ({ entity, className }) => {
  const dispatch = useDispatch();
  const { addToCart, isLoading: isAddingToCart } = useAddToCart();

  const [delta, setDelta] = useState(0);

  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const classes = classNames(className, 'NodeEventDetail');
  const nid = entity.getIn(['nid', 0, 'value'], '');
  const title = entity.getIn(['title', 0, 'value'], '');
  const subtitle = entity.getIn(['field_subtitle', 0, 'value']);
  const theme = entity.getIn(['field_theme', 0, 'entity']);
  const type = entity.getIn(
    ['field_event_type', 0, 'entity', 'name', 0, 'value'],
    'Event',
  );
  const body = entity.getIn(['body', 0, 'processed']);
  const stayNotifiedUrl = entity.getIn(['field_link', 0, 'alias'], entity.getIn(['field_link', 0, 'uri']));
  const stayNotifiedTitle = entity.getIn(['field_link', 0, 'title'], t('event.staynotified'));
  const campaign = entity.getIn(['field_campaign', 0, 'entity'], null);
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);
  const getStartedContent = entity.getIn(['field_get_started_content'], Immutable.Map()).concat(entity.getIn(['field_get_started_tax_content']));
  // Filtering events so we do not show full events.
  const events = useSelector((state) => state.getIn(
    ['view', 'event_dates', 'default', 'entities'],
    Immutable.List(),
  ).filter((event) => {
    const eventParticipants = event.get('participants', 0);
    const maxAllowedEventParticipants = event.getIn(
      ['field_maximum_participants', 0, 'value'],
      0,
    );
    const eventHasFreeSeats = maxAllowedEventParticipants - eventParticipants > 0;

    return (
      maxAllowedEventParticipants === AMOUNT_UNLIMITED || eventHasFreeSeats
    );
  }),
  );

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  const status = events.getIn([delta, 'field_status', 0, 'value'], 'open');
  const price = events.getIn([
    delta,
    'default_variation',
    0,
    'price',
    'number',
  ]);
  const location = events.getIn([delta, 'field_location', 0, 'value'], 'Nog niet bekend');
  const sku = events.getIn([delta, 'default_variation', 0, 'sku']);
  const participants = parseInt(events.getIn([delta, 'participants'], 0));
  const maxParticipants = parseInt(
    events.getIn([delta, 'field_maximum_participants', 0, 'value'], 0),
  );
  const freeSeats = maxParticipants - participants;

  const result = entity.getIn(['field_result', 0, 'value']);
  const targetAudience = entity.getIn(['field_target_audience', 0, 'value']);
  const accreditation = entity.getIn(['field_accreditation', 0, 'value']);
  const speakers = entity.getIn(['field_speakers', 0, 'value']);
  const program = entity.getIn(['field_program', 0, 'value']);
  const termsAndConditions = entity.getIn([
    'field_terms_and_conditions',
    0,
    'value',
  ]);

  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);

  const subscribeForEvent = async (values, form) => {
    // Use useAddToCart hook for proper conflict handling
    // Physical training events are NOT digital products
    await addToCart(
      {
        uuid: values.event_uuid,
        sku: sku || '',
        isDigitalProduct: false,
        title,
        price: parseFloat(price) || 0,
      },
      1, // quantity
      {
        skipRedirect: true, // Physical products don't redirect to checkout
      },
    );
    form.setSubmitting(false);
  };

  const dateOptions = useMemo(() => {
    const dateOpt = {};

    events.map((event) => {
      const uuid = event.getIn(['variations', 0, 'target_uuid']);
      const eventLocation = event.getIn(['field_location', 0, 'value'], 'Nog niet bekend');
      const dates = event.getIn(['field_date']);

      dateOpt[uuid] = DateString(dates, eventLocation);
    });
    return dateOpt;
  }, [events]);

  const renderPrice = (number) => {
    if (number - Math.floor(number) !== 0) {
      // It's a float, add decimals
      return `€ ${parseFloat(price).toLocaleString('nl-NL', {
        minimumFractionDigits: 2,
      })}`;
    }

    return `€ ${parseInt(price)},-`;
  };

  const currentSelectedEventUuid = Object.keys(dateOptions)[delta];

  useEffect(() => {

    dispatch(viewGet('event_dates', 'default', 0, [nid], {}));

  }, [dispatch, nid]);

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Container>
        {entity.getIn(['field_media_image', 0]) && (
          <div className="NodeEventDetail__hero">
            <Image
              className="NodeEventDetail__hero__image"
              src={{
                [SCREEN_MOBILE]: entity.getIn(['field_media_image', 0, 'styles', 'hero_tablet']),
                [SCREEN_NORMAL]: entity.getIn(['field_media_image', 0, 'styles', 'hero_image']),
              }}
              alt={entity.getIn(['field_media_image', 0, 'alt'], '')}
            />
          </div>
        )}
        <div className="row NodeEventDetail__content__wrapper" id="main">
          <div className="col-12">
            <div className="row">
              <div className="col-12 col-md-8">
                <div className="NodeEventDetail__content__header">
                  <NodeMetadata
                    icon="icon-copy"
                    displayType={type}
                    theme={theme}
                  />
                  <h1>{title}</h1>
                  {subtitle && (
                    <div className="NodeEventDetail__subtitle">{subtitle}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-5 col-lg-4 NodeEventDetail__information">
            <div className="row">
              <div className="col-12">
                <div className="NodeEventDetail__information__subscribe">
                  <h5>{title}</h5>
                  {subtitle && (
                    <div className="NodeEventDetail__information__subscribe__subtitle">
                      {subtitle}
                    </div>
                  )}

                  {Object.keys(dateOptions).length > 0 && (
                    <FormikWrapper
                      initialValues={{ event_uuid: currentSelectedEventUuid }}
                      onSubmit={subscribeForEvent}
                    >
                      <FormSelect
                        options={dateOptions}
                        defaultValue={currentSelectedEventUuid}
                        fieldName="event_uuid"
                        isClearable={false}
                        onChange={(value) => {
                          setDelta(Object.keys(dateOptions).indexOf(value));
                        }}
                      />
                      {typeof currentSelectedEventUuid === 'undefined'
                        || (maxParticipants !== 0
                          && (freeSeats === 0
                            || status === 'closed'
                            || status === 'cancelled'
                            || status === 'full'))
                        ? (
                          <Button
                            primary={false}
                            className="Button--primary"
                            disabled
                          >
                            Gesloten
                          </Button>
                        ) : (
                          <Button
                            primary={false}
                            type="submit"
                            className="Button--primary icon-after icon-arrow-right icon-variant-2"
                            disabled={isAddingToCart}
                          >
                            Inschrijven
                          </Button>
                        )}
                    </FormikWrapper>
                  )}

                  {events.size < 1 && stayNotifiedUrl && (
                    <CoreLink className="Button Button--primary icon-after icon-arrow-right icon-variant-2" to={stayNotifiedUrl}>
                      {stayNotifiedTitle}
                    </CoreLink>
                  )}

                </div>
              </div>
              <div className="col-12 NodeEventDetail__information__list">
                {typeof currentSelectedEventUuid !== 'undefined' && (
                  <PropertiesTable>
                    {location && (
                      <PropertiesTable.Property
                        label="Locatie"
                        value={location}
                      />
                    )}
                    {maxParticipants > 0 && freeSeats > 0 && (
                      <PropertiesTable.Property
                        label="Aantal plekken beschikbaar"
                        value={`${freeSeats}`}
                      />
                    )}
                    {price && (
                      <PropertiesTable.Property
                        label="Prijs"
                        value={renderPrice(price)}
                        suffix="excl. btw"
                      />
                    )}
                  </PropertiesTable>
                )}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-7 col-lg-8 NodeEventDetail__content">
            {body && (
              <WysiwygField
                property="schema:text"
                className="NodeEventDetail__body subheading2"
                html={body}
              />
            )}

            {targetAudience && (
              <Faq question="Doelgroep" answer={targetAudience} />
            )}
            {result && <Faq question="Doel / resultaat" answer={result} />}
            {program && <Faq question="Programma" answer={program} />}
            {speakers && <Faq question="Sprekers" answer={speakers} />}
            {accreditation && (
              <Faq question="Accreditatiepunten" answer={accreditation} />
            )}
            {termsAndConditions && (
              <Faq
                question="Algemene voorwaarden"
                answer={termsAndConditions}
              />
            )}

            {events.count() > 0 && (
              <div className="NodeEventDetail__events">
                {events.size < 2 ? <h3>Datum</h3> : <h3>Data</h3>}
                {events.map((event, subIndex) => {
                  const subUuid = event.getIn(['uuid', 0], subIndex);
                  return <Training key={subUuid} entity={event} />;
                })}
              </div>
            )}
          </div>
        </div>
      </Container>

      <div className="NodeContentWrapper">

        {entity.getIn(['field_paragraphs', 0]) && (
          <div className="NodeEventDetail__paragraphs">
            {entity.getIn(['field_paragraphs']).map((item, index) => {
              if (showMemberWall && index > 0) return;
              const paragraphId = item.get('target_id');
              const paragraph = item.getIn(['entity']);

              return (
                <Paragraph
                  key={paragraphId}
                  entity={paragraph}
                  id={paragraphId}
                />
              );
            })}
          </div>
        )}

        {campaign && (
          <CampaignBlock entity={campaign} />
        )}

        <GetStarted
          title={entity.getIn(['field_get_started_title', 0, 'value'])}
          subtitle={entity.getIn(['field_get_started_subtitle', 0, 'value'])}
          content={getStartedContent}
        />

        {entity.getIn(['field_paragraph_feedback', 0]) && (
          <div className="NodeEventDetail__feedback">
            {entity.getIn(['field_paragraph_feedback']).map((item, index) => {
              if (showMemberWall && index > 0) return;
              const paragraphId = item.get('target_id');
              const paragraph = item.getIn(['entity']);

              return (
                <Paragraph
                  key={paragraphId}
                  entity={paragraph}
                  id={paragraphId}
                />
              );
            })}
          </div>
        )}

        <Tags subjects={subjects} workfields={workfields} />
        {theme && (
          <Container>
            <div className="row">
              <div className="col-12">
                <Taxonomy
                  id={theme.getIn(['tid', 0, 'value'])}
                  entity={theme}
                  viewMode="card"
                />
              </div>
            </div>
          </Container>
        )}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
            </div>
          </div>
        </Container>
      </div>

      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}
    </article>
  );
};

export default NodeEventDetail;
